import { Fragment, useState } from "react";
import {
    CheckCircle2Icon,
    InfoIcon,
    OctagonXIcon,
    TriangleAlertIcon,
    type LucideIcon,
} from "lucide-react";

import Layout from "../../components/Layout";
import { Heading } from "@/components/typography/heading";
import { Alert, AlertAction, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { toast } from "@/components/ui/toast";

type AlertVariant = "default" | "destructive" | "success" | "warning";

type VariantSpec = {
    variant: AlertVariant;
    icon: LucideIcon;
    title: string;
    action: string;
};

const VARIANTS: VariantSpec[] = [
    { variant: "default", icon: InfoIcon, title: "Heads up", action: "Enable" },
    { variant: "success", icon: CheckCircle2Icon, title: "Changes saved", action: "Undo" },
    { variant: "warning", icon: TriangleAlertIcon, title: "Subscription expiring", action: "Renew" },
    { variant: "destructive", icon: OctagonXIcon, title: "Payment failed", action: "Retry" },
];

const BODY =
    "Your payment of $29.99 has been processed. A receipt has been sent to your email address.";

function RowLabel({ children }: { children: React.ReactNode }) {
    return <code className="text-sm text-muted-foreground">{children}</code>;
}

export default function Alerts() {
    const [graySurface, setGraySurface] = useState(false);

    return (
        <Layout gray={graySurface}>
            <div className="space-y-10 p-4">
                <div>
                    <Heading type="h1" className="mb-2">Alerts</Heading>
                    <p className="mb-4 max-w-2xl text-muted-foreground">
                        Soft semantic recipe: <code className="text-sm">*-25</code> wash,
                        accent on border and icon, body stays muted.{" "}
                        <code className="text-sm">default</code> is the registry plain card;
                        success / warning / destructive share the tinted treatment. Feedback
                        hues: success 156, warning 86, destructive 20 (see{" "}
                        <code className="text-sm">index.css</code>).
                    </p>
                    <Button variant="outline" size="sm" onClick={() => setGraySurface(!graySurface)}>
                        Surface: {graySurface ? "gray-100" : "background"}
                    </Button>
                </div>

                <section className="space-y-4">
                    <Heading type="h2">Toasts (soft chrome)</Heading>
                    <p className="max-w-2xl text-muted-foreground">
                        Overlay host (bottom-right stack, timed dismiss). Soft severity chrome
                        matches Alert — same <code className="text-sm">*-25</code> wash and
                        accent icon/border; host behavior is what differs. Fire a few in a row
                        to see stacking vs the inline alert below.
                    </p>
                    <div className="space-x-2">
                        <Button
                            size="sm"
                            onClick={() =>
                                toast.add({
                                    type: "success",
                                    title: "Changes saved",
                                    description: "Your payment of $29.99 has been processed.",
                                })
                            }
                        >
                            Success
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                                toast.add({
                                    type: "info",
                                    title: "Heads up",
                                    description: "A new version may be available later.",
                                })
                            }
                        >
                            Info
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                                toast.add({
                                    type: "warning",
                                    title: "Subscription expiring",
                                    description: "Renew before the period ends.",
                                })
                            }
                        >
                            Warning
                        </Button>
                        <Button
                            size="sm"
                            variant="outline"
                            onClick={() =>
                                toast.add({
                                    type: "error",
                                    title: "Payment failed",
                                    description: "Something went wrong. Please try again.",
                                    actionProps: { children: "Retry" },
                                })
                            }
                        >
                            Error + action
                        </Button>
                    </div>

                    <div className="space-y-2 border-t border-border pt-4">
                        <RowLabel>inline alert for comparison — same copy as success toast</RowLabel>
                        <Alert variant="success">
                            <CheckCircle2Icon />
                            <AlertTitle>Changes saved</AlertTitle>
                            <AlertDescription>
                                Your payment of $29.99 has been processed.
                            </AlertDescription>
                        </Alert>
                    </div>
                </section>

                <section className="space-y-4">
                    <Heading type="h2">Variants</Heading>
                    {VARIANTS.map(({ variant, icon: Icon, title, action }) => (
                        <div key={variant} className="space-y-2">
                            <RowLabel>{variant}</RowLabel>
                            <Alert variant={variant}>
                                <Icon />
                                <AlertTitle>{title}</AlertTitle>
                                <AlertDescription>{BODY}</AlertDescription>
                                <AlertAction>
                                    <Button variant="outline" size="xs">{action}</Button>
                                </AlertAction>
                            </Alert>
                        </div>
                    ))}
                </section>

                <section className="space-y-4">
                    <div>
                        <Heading type="h2" className="mb-2">Composition</Heading>
                        <p className="max-w-2xl text-muted-foreground">
                            Title only, description only, with an action, and with a long body that
                            wraps — the action slot is absolutely positioned, so check it does not
                            collide with wrapped text.
                        </p>
                    </div>

                    <Alert variant="success">
                        <CheckCircle2Icon />
                        <AlertTitle>Title only</AlertTitle>
                    </Alert>

                    <Alert variant="success">
                        <AlertDescription>Description only, no icon and no title.</AlertDescription>
                    </Alert>

                    <Alert variant="warning">
                        <TriangleAlertIcon />
                        <AlertTitle>With an action</AlertTitle>
                        <AlertDescription>{BODY}</AlertDescription>
                        <AlertAction>
                            <Button variant="outline" size="xs">Renew</Button>
                        </AlertAction>
                    </Alert>

                    <Alert variant="destructive">
                        <OctagonXIcon />
                        <AlertTitle>Long body with an action</AlertTitle>
                        <AlertDescription>
                            {BODY} {BODY} {BODY}
                        </AlertDescription>
                        <AlertAction>
                            <Button variant="outline" size="xs">Retry</Button>
                        </AlertAction>
                    </Alert>
                </section>

                <section className="space-y-4">
                    <div>
                        <Heading type="h2" className="mb-2">Skim test</Heading>
                        <p className="max-w-2xl text-muted-foreground">
                            Identical copy — severity should register before you read.
                        </p>
                    </div>

                    {VARIANTS.map(({ variant, icon: Icon }) => (
                        <Fragment key={variant}>
                            <Alert variant={variant}>
                                <Icon />
                                <AlertTitle>The quick brown fox</AlertTitle>
                                <AlertDescription>
                                    Jumps over the lazy dog and keeps on running.
                                </AlertDescription>
                            </Alert>
                        </Fragment>
                    ))}
                </section>
            </div>
        </Layout>
    );
}
